// Bears Energy — Google Analytics 4 Tracking Library
// Tracks: pageviews, screen time, login conversions, simulation steps, proposals, PDF generation, button clicks, logins/logouts, user registrations

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

// Helper: safely call gtag only in production and when available
function gtag(...args: any[]) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

// ─── Page View ────────────────────────────────────────────────────────────────

export function trackPageView(url: string, title?: string) {
  if (!GA_ID) return;
  gtag('event', 'page_view', {
    page_path: url,
    page_title: title,
    send_to: GA_ID,
  });
}

// ─── Auth Events ──────────────────────────────────────────────────────────────

export function trackLogin(method: 'email' | 'google') {
  gtag('event', 'login', {
    method,
  });
}

export function trackLogout() {
  gtag('event', 'logout');
}

export function trackSignUp(method: 'email' | 'google') {
  gtag('event', 'sign_up', {
    method,
  });
}

// ─── Simulation Funnel ────────────────────────────────────────────────────────

// Step 1: Upload de Fatura
export function trackSimulationStep1Start(projectId?: string) {
  gtag('event', 'simulation_step1_start', {
    event_category: 'simulation_funnel',
    event_label: 'Upload de Fatura',
    project_id: projectId,
  });
}

export function trackSimulationStep1Complete(projectId?: string) {
  gtag('event', 'simulation_step1_complete', {
    event_category: 'simulation_funnel',
    event_label: 'Upload de Fatura Concluído',
    project_id: projectId,
  });
  // GA4 conversion event
  gtag('event', 'simulation_funnel_step', {
    step_number: 1,
    step_name: 'upload_fatura',
    project_id: projectId,
  });
}

// Step 2: Parâmetros Técnicos e Econômicos
export function trackSimulationStep2Start(projectId?: string) {
  gtag('event', 'simulation_step2_start', {
    event_category: 'simulation_funnel',
    event_label: 'Parâmetros Técnicos',
    project_id: projectId,
  });
}

export function trackSimulationStep2Complete(projectId?: string) {
  gtag('event', 'simulation_step2_complete', {
    event_category: 'simulation_funnel',
    event_label: 'Parâmetros Técnicos Concluídos',
    project_id: projectId,
  });
  gtag('event', 'simulation_funnel_step', {
    step_number: 2,
    step_name: 'parametros_tecnicos',
    project_id: projectId,
  });
}

// Step 3: Revisão / Resultados
export function trackSimulationStep3Start(projectId?: string) {
  gtag('event', 'simulation_step3_start', {
    event_category: 'simulation_funnel',
    event_label: 'Revisão de Resultados',
    project_id: projectId,
  });
}

export function trackSimulationStep3Complete(projectId?: string) {
  gtag('event', 'simulation_step3_complete', {
    event_category: 'simulation_funnel',
    event_label: 'Revisão Concluída',
    project_id: projectId,
  });
  gtag('event', 'simulation_funnel_step', {
    step_number: 3,
    step_name: 'revisao_resultados',
    project_id: projectId,
  });
}

// Step 4: Proposta Gerada
export function trackSimulationStep4Start(projectId?: string) {
  gtag('event', 'simulation_step4_start', {
    event_category: 'simulation_funnel',
    event_label: 'Proposta Técnico-Comercial',
    project_id: projectId,
  });
}

export function trackSimulationStep4Complete(projectId?: string, simulationId?: string) {
  gtag('event', 'simulation_step4_complete', {
    event_category: 'simulation_funnel',
    event_label: 'Proposta Concluída',
    project_id: projectId,
    simulation_id: simulationId,
  });
  gtag('event', 'simulation_funnel_step', {
    step_number: 4,
    step_name: 'proposta_gerada',
    project_id: projectId,
    simulation_id: simulationId,
  });
}

// ─── Simulation Creation ──────────────────────────────────────────────────────

export function trackSimulationCreated(projectId?: string, simulationId?: string) {
  gtag('event', 'simulation_created', {
    event_category: 'simulation',
    project_id: projectId,
    simulation_id: simulationId,
  });
}

// ─── Proposal Events ──────────────────────────────────────────────────────────

export function trackProposalViewed(projectId?: string, simulationId?: string) {
  gtag('event', 'proposal_viewed', {
    event_category: 'proposal',
    project_id: projectId,
    simulation_id: simulationId,
  });
}

export function trackProposalFinalized(projectId?: string, simulationId?: string) {
  gtag('event', 'proposal_finalized', {
    event_category: 'proposal',
    project_id: projectId,
    simulation_id: simulationId,
  });
}

// ─── PDF Generation ───────────────────────────────────────────────────────────

export function trackPDFGenerationStart(projectId?: string, simulationId?: string) {
  gtag('event', 'pdf_generation_start', {
    event_category: 'pdf',
    project_id: projectId,
    simulation_id: simulationId,
  });
}

export function trackPDFGenerationComplete(projectId?: string, simulationId?: string) {
  gtag('event', 'pdf_generation_complete', {
    event_category: 'pdf',
    project_id: projectId,
    simulation_id: simulationId,
  });
}

// ─── Button Clicks ────────────────────────────────────────────────────────────

export function trackButtonClick(buttonId: string, label: string, page?: string) {
  gtag('event', 'button_click', {
    event_category: 'engagement',
    button_id: buttonId,
    button_label: label,
    page_path: page || (typeof window !== 'undefined' ? window.location.pathname : ''),
  });
}

// ─── User Properties ──────────────────────────────────────────────────────────

export function setUserProperties(userId?: string, role?: string) {
  if (!userId) return;
  gtag('config', GA_ID, {
    user_id: userId,
  });
  gtag('set', 'user_properties', {
    user_role: role || 'integrador',
  });
}

// ─── Screen Time ──────────────────────────────────────────────────────────────

export function trackScreenTime(screenName: string, durationSeconds: number) {
  gtag('event', 'screen_time', {
    event_category: 'engagement',
    screen_name: screenName,
    duration_seconds: durationSeconds,
  });
}
